/* TOO MUCH: colour swatch buttons on product cards (colour siblings, and colour variants on rail cards).
   Hover (fine pointer) previews the colour's photo. Click / tap selects it in place: the photo switches, the ring
   moves (aria-pressed), the card links point to that colour, and title / price follow when they differ.
   Swatches never navigate; only the photo and the title do. */
if (!customElements.get('tm-swatch-buttons')) {
  const productCache = new Map();

  const fetchProduct = (url) => {
    const clean = url.split('?')[0];
    if (!productCache.has(clean)) {
      productCache.set(
        clean,
        fetch(`${clean}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(clean);
  };

  /* Round 3 QA: prices follow the shop money format (Dhs. 18,650), as printed by Liquid on the same card; the
     Intl fallback gave "AED 18,650" next to "Dhs." prices */
  const groupDigits = (cents, decimals, thousands, decimal) => {
    const [whole, part] = (cents / 100).toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decimals > 0 ? grouped + decimal + part : grouped;
  };

  const formatMoney = (cents, currency, format) => {
    if (format && /\{\{\s*\w+\s*\}\}/.test(format)) {
      return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
        switch (key) {
          case 'amount_no_decimals': return groupDigits(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return groupDigits(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return groupDigits(cents, 0, '.', ',');
          case 'amount_with_space_separator': return groupDigits(cents, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator': return groupDigits(cents, 0, ' ', ',');
          case 'amount_with_apostrophe_separator': return groupDigits(cents, 2, "'", '.');
          case 'currency': return currency || '';
          default: return groupDigits(cents, 2, ',', '.');
        }
      });
    }
    const value = cents / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'AED',
        maximumFractionDigits: value % 1 === 0 ? 0 : 2,
      }).format(value);
    } catch (e) {
      return String(value);
    }
  };

  class TmSwatchButtons extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.card = this.closest('[data-tm-card]') || this.closest('product-card') || this.parentElement;
      this.img = this.card && this.card.querySelector('[data-tm-card-img], .tm-rcard__img, .card-gallery img');
      this.selected = this.querySelector('[aria-pressed="true"]');
      this.baseImage = this.img ? { src: this.img.getAttribute('src'), srcset: this.img.getAttribute('srcset') } : null;
      this.fine = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onOver);
      this.addEventListener('pointerout', this.onOut);
    }

    showImage(url) {
      if (!this.img) return;
      if (!url) {
        if (!this.baseImage) return;
        this.img.src = this.baseImage.src;
        if (this.baseImage.srcset) this.img.setAttribute('srcset', this.baseImage.srcset);
        return;
      }
      this.img.removeAttribute('srcset');
      this.img.src = url;
    }

    current() {
      return this.selected && this.selected.dataset.image;
    }

    onOver = (event) => {
      if (!this.fine.matches) return;
      const button = event.target.closest('button[data-image]');
      if (button && this.contains(button)) this.showImage(button.dataset.image);
    };

    onOut = (event) => {
      if (!this.fine.matches) return;
      const button = event.target.closest('button[data-image]');
      if (!button || (event.relatedTarget && button.contains(event.relatedTarget))) return;
      this.showImage(this.current());
    };

    onClick = (event) => {
      const button = event.target.closest('button[data-url]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.selected) this.selected.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-pressed', 'true');
      this.selected = button;
      this.showImage(button.dataset.image);

      const url = button.dataset.url;
      if (this.card) {
        this.card.querySelectorAll('a[href*="/products/"], a[data-tm-card-link]').forEach((a) => {
          a.setAttribute('href', url);
        });
      }
      // the wishlist heart on the card follows the chosen colour sibling (assets/tm-wishlist.js)
      const heart = this.card && this.card.querySelector('tm-wishlist-button');
      if (heart && button.dataset.handle) {
        heart.dataset.handle = button.dataset.handle;
        if (typeof heart.sync === 'function') heart.sync();
      }

      const label = this.querySelector('[data-tm-swatch-label]');
      if (label) label.textContent = button.dataset.name || '';

      if (button.dataset.title || button.dataset.price) {
        this.update(button.dataset.title, button.dataset.priceText);
      } else if (button.dataset.sibling !== undefined) {
        fetchProduct(url).then((product) => {
          if (!product || this.selected !== button) return;
          const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AED';
          this.update(product.title, formatMoney(product.price, currency, this.dataset.moneyFormat));
        });
      }
    };

    update(title, priceText) {
      if (!this.card) return;
      if (title) {
        const t = this.card.querySelector('[data-tm-card-title], a[ref="productTitleLink"] p');
        if (t) t.textContent = title;
      }
      if (priceText) {
        const p = this.card.querySelector('[data-tm-card-price], product-price .price, .price');
        if (p) p.textContent = priceText;
      }
    }
  }

  customElements.define('tm-swatch-buttons', TmSwatchButtons);
}
